import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  CircularProgress,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";

// ---------------- Poisson helpers ----------------
function factorial(n) {
  return n <= 1 ? 1 : n * factorial(n - 1);
}
function poissonP(k, lambda) {
  if (lambda <= 0) return k === 0 ? 1 : 0;
  return Math.exp(-lambda) * Math.pow(lambda, k) / factorial(k);
}
function calculateScoreProbs(lambdaHome, lambdaAway, maxGoals = 5) {
  const probs = [];
  for (let i = 0; i <= maxGoals; i++) {
    for (let j = 0; j <= maxGoals; j++) {
      probs.push({
        home: i,
        away: j,
        prob: poissonP(i, lambdaHome) * poissonP(j, lambdaAway),
      });
    }
  }
  return probs;
}
function normalizeProbs(list) {
  const sum = list.reduce((s, x) => s + x.prob, 0) || 1;
  return list.map((x) => ({ ...x, prob: x.prob / sum }));
}
function outcomeProbs(scoreProbs) {
  let homeWin = 0, draw = 0, awayWin = 0;
  scoreProbs.forEach((s) => {
    if (s.home > s.away) homeWin += s.prob;
    else if (s.home === s.away) draw += s.prob;
    else awayWin += s.prob;
  });
  return { homeWin, draw, awayWin };
}
function cleanSheetProb(lambda) {
  return poissonP(0, lambda);
}
function formIndex(avgScored, conceded, csProb) {
  const attackScore = avgScored / (avgScored + 2);
  const defenseScore = 1 - conceded / (conceded + 2);
  return 0.5 * attackScore + 0.3 * defenseScore + 0.2 * csProb;
}

// ---------- Post-processing utilities ----------
function boostTopScores(scoreProbs, topN = 1, boostFactor = 2, neighborRadius = 1) {
  let list = scoreProbs.map((s) => ({ ...s }));
  const sorted = [...list].sort((a, b) => b.prob - a.prob);
  const top = sorted.slice(0, topN);

  top.forEach((t) => {
    list = list.map((s) => {
      if (s.home === t.home && s.away === t.away) {
        return { ...s, prob: s.prob * boostFactor };
      }
      if (
        Math.abs(s.home - t.home) <= neighborRadius &&
        Math.abs(s.away - t.away) <= neighborRadius
      ) {
        // smaller boost to neighbors
        return { ...s, prob: s.prob * (1 + (boostFactor - 1) / 2) };
      }
      return s;
    });
  });

  return normalizeProbs(list);
}

function clusterBuckets(scoreProbs) {
  let home = 0, draw = 0, away = 0;
  scoreProbs.forEach((s) => {
    if (s.home > s.away) home += s.prob;
    else if (s.home === s.away) draw += s.prob;
    else away += s.prob;
  });
  return { home, draw, away };
}

// color selection for circular progress
function circleColor(value, isDraw = false, drawThreshold = 0.6) {
  // For draw circles, use drawThreshold to decide green
  if (isDraw) {
    if (value >= drawThreshold) return "success";
    if (value >= 0.3) return "warning";
    return "error";
  }
  // For non-draw: green >= 50%, orange 30-49.9%, red < 30%
  if (value >= 0.5) return "success";
  if (value >= 0.3) return "warning";
  return "error";
}

function StatCircle({ label, value, isDraw = false, drawThreshold = 0.6 }) {
  const color = circleColor(value, isDraw, drawThreshold);
  return (
    <Box textAlign="center" m={1}>
      <CircularProgress
        variant="determinate"
        value={Math.min(100, value * 100)}
        color={color}
        size={80}
        thickness={5}
      />
      <Typography variant="body2" mt={1}>
        {label}: {(value * 100).toFixed(1)}%
      </Typography>
    </Box>
  );
}

// ---------------- Main component ----------------
export default function HybridPoissonPredictor() {
  // Team names
  const [homeName, setHomeName] = useState("Jamaica");
  const [awayName, setAwayName] = useState("Trinidad and Tobago");

  // Avg goals (scored) per half
  const [home1HAvg, setHome1HAvg] = useState(1.0);
  const [away1HAvg, setAway1HAvg] = useState(0.8);
  const [home2HAvg, setHome2HAvg] = useState(2.0);
  const [away2HAvg, setAway2HAvg] = useState(0.4);

  // Conceded averages
  const [home1HConcede, setHome1HConcede] = useState(0.4);
  const [away1HConcede, setAway1HConcede] = useState(1.0);
  const [home2HConcede, setHome2HConcede] = useState(1.4);
  const [away2HConcede, setAway2HConcede] = useState(1.0);

  // Clean sheet overrides (percent 0-100) — optional
  const [home1HCS, setHome1HCS] = useState("60");
  const [away1HCS, setAway1HCS] = useState("80");
  const [home2HCS, setHome2HCS] = useState("40");
  const [away2HCS, setAway2HCS] = useState("20");

  // Calibration knobs
  const [homeAdv, setHomeAdv] = useState(0.15); // additive λ to home halves
  const [lambdaShrink, setLambdaShrink] = useState(0.9); // 0..1
  const [boostTopFactor, setBoostTopFactor] = useState(2.2);
  const [boostTopN, setBoostTopN] = useState(1);
  const [neighborRadius, setNeighborRadius] = useState(1);
  const [maxGoals, setMaxGoals] = useState(5);
  const [drawThreshold, setDrawThreshold] = useState(0.6);

  // --- Build base lambdas (simple average of team's scored and opponent conceded)
  const baseHomeLambda1H = (home1HAvg + away1HConcede) / 2;
  const baseAwayLambda1H = (away1HAvg + home1HConcede) / 2;
  const baseHomeLambda2H = (home2HAvg + away2HConcede) / 2;
  const baseAwayLambda2H = (away2HAvg + home2HConcede) / 2;

  // CS overrides -> probabilities 0..1 (if empty, compute from Poisson)
  const home1HCSprob = home1HCS !== "" ? Math.max(0, Math.min(100, parseFloat(home1HCS))) / 100 : cleanSheetProb(baseAwayLambda1H);
  const away1HCSprob = away1HCS !== "" ? Math.max(0, Math.min(100, parseFloat(away1HCS))) / 100 : cleanSheetProb(baseHomeLambda1H);
  const home2HCSprob = home2HCS !== "" ? Math.max(0, Math.min(100, parseFloat(home2HCS))) / 100 : cleanSheetProb(baseAwayLambda2H);
  const away2HCSprob = away2HCS !== "" ? Math.max(0, Math.min(100, parseFloat(away2HCS))) / 100 : cleanSheetProb(baseHomeLambda2H);

  // Adjust lambda using opponent clean-sheet and shrink factor
  function adjustLambda(baseLambda, oppCS) {
    const csFactor = 1 - oppCS * 0.5; // up to 50% reduction if oppCS = 1.0
    const adjusted = baseLambda * csFactor;
    // shrink towards adjusted by lambdaShrink
    return baseLambda * (1 - lambdaShrink) + adjusted * lambdaShrink;
  }

  // Apply adjustments + home advantage
  let adjHomeLambda1H = adjustLambda(baseHomeLambda1H, away1HCSprob) + homeAdv;
  let adjAwayLambda1H = adjustLambda(baseAwayLambda1H, home1HCSprob);
  let adjHomeLambda2H = adjustLambda(baseHomeLambda2H, away2HCSprob) + homeAdv;
  let adjAwayLambda2H = adjustLambda(baseAwayLambda2H, home2HCSprob);

  // clamp small
  adjHomeLambda1H = Math.max(0.01, adjHomeLambda1H);
  adjAwayLambda1H = Math.max(0.01, adjAwayLambda1H);
  adjHomeLambda2H = Math.max(0.01, adjHomeLambda2H);
  adjAwayLambda2H = Math.max(0.01, adjAwayLambda2H);

  // --- Score distributions (apply boosting + normalization)
  let scoreProbs1H = calculateScoreProbs(adjHomeLambda1H, adjAwayLambda1H, maxGoals);
  let scoreProbs2H = calculateScoreProbs(adjHomeLambda2H, adjAwayLambda2H, maxGoals);

  scoreProbs1H = boostTopScores(normalizeProbs(scoreProbs1H), boostTopN, boostTopFactor, neighborRadius);
  scoreProbs2H = boostTopScores(normalizeProbs(scoreProbs2H), boostTopN, boostTopFactor, neighborRadius);

  // Fulltime uses sum of adjusted halves
  const adjHomeLambdaFT = adjHomeLambda1H + adjHomeLambda2H;
  const adjAwayLambdaFT = adjAwayLambda1H + adjAwayLambda2H;
  let scoreProbsFT = calculateScoreProbs(adjHomeLambdaFT, adjAwayLambdaFT, maxGoals);
  scoreProbsFT = boostTopScores(normalizeProbs(scoreProbsFT), boostTopN, boostTopFactor, neighborRadius);

  // final normalization (safety)
  scoreProbs1H = normalizeProbs(scoreProbs1H);
  scoreProbs2H = normalizeProbs(scoreProbs2H);
  scoreProbsFT = normalizeProbs(scoreProbsFT);

  // Outcomes (aggregates)
  const out1H = outcomeProbs(scoreProbs1H);
  const out2H = outcomeProbs(scoreProbs2H);
  const outFT = outcomeProbs(scoreProbsFT);

  // Form indexes for circles
  const homeForm1H = formIndex(home1HAvg, home1HConcede, home1HCSprob);
  const awayForm1H = formIndex(away1HAvg, away1HConcede, away1HCSprob);
  const homeForm2H = formIndex(home2HAvg, home2HConcede, home2HCSprob);
  const awayForm2H = formIndex(away2HAvg, away2HConcede, away2HCSprob);
  const homeOverallForm = (homeForm1H + homeForm2H) / 2;
  const awayOverallForm = (awayForm1H + awayForm2H) / 2;

  // Top-3 results helper
  function topResults(probs, n = 3) {
    return [...probs].sort((a, b) => b.prob - a.prob).slice(0, n);
  }
  const top1H = topResults(scoreProbs1H, 3);
  const top2H = topResults(scoreProbs2H, 3);
  const topFT = topResults(scoreProbsFT, 3);

  // Clusters
  const cluster1H = clusterBuckets(scoreProbs1H);
  const cluster2H = clusterBuckets(scoreProbs2H);
  const clusterFT = clusterBuckets(scoreProbsFT);

  function renderCluster(cluster) {
    // Use stricter draw threshold for labeling Draw cluster
    if (cluster.draw >= drawThreshold && cluster.draw > cluster.home && cluster.draw > cluster.away) return "Draw";
    if (cluster.home > cluster.away) return "Home Win";
    return "Away Win";
  }

  // UI helpers
  function renderTopList(title, topList) {
    return (
      <Box mb={1}>
        <Typography variant="subtitle1">{title}</Typography>
        {topList.map((r, i) => (
          <Typography key={i}>
            {homeName} {r.home} - {r.away} {awayName} ({(r.prob * 100).toFixed(2)}%)
          </Typography>
        ))}
      </Box>
    );
  }

  // tables render
  function renderTable(title, probs) {
    return (
      <Box mb={2}>
        <Typography variant="h6">{title} Top Probabilities</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Score</TableCell>
              <TableCell>Probability</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {probs
              .slice()
              .sort((a, b) => b.prob - a.prob)
              .slice(0, 8)
              .map((r, idx) => (
                <TableRow key={idx}>
                  <TableCell>{r.home} - {r.away}</TableCell>
                  <TableCell>{(r.prob * 100).toFixed(2)}%</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Hybrid Poisson Predictor — Calibrated
      </Typography>

      {/* Inputs */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6}><TextField fullWidth label="Home Team" value={homeName} onChange={(e) => setHomeName(e.target.value)} /></Grid>
        <Grid item xs={6}><TextField fullWidth label="Away Team" value={awayName} onChange={(e) => setAwayName(e.target.value)} /></Grid>

        <Grid item xs={6}><TextField fullWidth label="Home 1H Avg Goals" type="number" value={home1HAvg} onChange={(e) => setHome1HAvg(parseFloat(e.target.value) || 0)} /></Grid>
        <Grid item xs={6}><TextField fullWidth label="Away 1H Avg Goals" type="number" value={away1HAvg} onChange={(e) => setAway1HAvg(parseFloat(e.target.value) || 0)} /></Grid>
        <Grid item xs={6}><TextField fullWidth label="Home 2H Avg Goals" type="number" value={home2HAvg} onChange={(e) => setHome2HAvg(parseFloat(e.target.value) || 0)} /></Grid>
        <Grid item xs={6}><TextField fullWidth label="Away 2H Avg Goals" type="number" value={away2HAvg} onChange={(e) => setAway2HAvg(parseFloat(e.target.value) || 0)} /></Grid>

        <Grid item xs={6}><TextField fullWidth label="Home 1H Conceded" type="number" value={home1HConcede} onChange={(e) => setHome1HConcede(parseFloat(e.target.value) || 0)} /></Grid>
        <Grid item xs={6}><TextField fullWidth label="Away 1H Conceded" type="number" value={away1HConcede} onChange={(e) => setAway1HConcede(parseFloat(e.target.value) || 0)} /></Grid>
        <Grid item xs={6}><TextField fullWidth label="Home 2H Conceded" type="number" value={home2HConcede} onChange={(e) => setHome2HConcede(parseFloat(e.target.value) || 0)} /></Grid>
        <Grid item xs={6}><TextField fullWidth label="Away 2H Conceded" type="number" value={away2HConcede} onChange={(e) => setAway2HConcede(parseFloat(e.target.value) || 0)} /></Grid>

        <Grid item xs={6}><TextField fullWidth label="Home 1H CS % (optional)" type="number" value={home1HCS} onChange={(e) => setHome1HCS(e.target.value)} /></Grid>
        <Grid item xs={6}><TextField fullWidth label="Away 1H CS % (optional)" type="number" value={away1HCS} onChange={(e) => setAway1HCS(e.target.value)} /></Grid>
        <Grid item xs={6}><TextField fullWidth label="Home 2H CS % (optional)" type="number" value={home2HCS} onChange={(e) => setHome2HCS(e.target.value)} /></Grid>
        <Grid item xs={6}><TextField fullWidth label="Away 2H CS % (optional)" type="number" value={away2HCS} onChange={(e) => setAway2HCS(e.target.value)} /></Grid>

        <Grid item xs={6}><TextField fullWidth label="Home Advantage (add λ)" type="number" value={homeAdv} onChange={(e) => setHomeAdv(parseFloat(e.target.value) || 0)} /></Grid>
        <Grid item xs={6}><TextField fullWidth label="Lambda shrink (0-1)" type="number" value={lambdaShrink} onChange={(e) => setLambdaShrink(Math.max(0, Math.min(1, parseFloat(e.target.value) || 0)))} /></Grid>
        <Grid item xs={6}><TextField fullWidth label="Boost top factor" type="number" value={boostTopFactor} onChange={(e) => setBoostTopFactor(parseFloat(e.target.value) || 1)} /></Grid>
        <Grid item xs={6}><TextField fullWidth label="Boost top N" type="number" value={boostTopN} onChange={(e) => setBoostTopN(Math.max(1, parseInt(e.target.value) || 1))} /></Grid>
        <Grid item xs={6}><TextField fullWidth label="Neighbor radius" type="number" value={neighborRadius} onChange={(e) => setNeighborRadius(Math.max(0, parseInt(e.target.value) || 0))} /></Grid>
        <Grid item xs={6}><TextField fullWidth label="Max goals in distribution" type="number" value={maxGoals} onChange={(e) => setMaxGoals(Math.max(1, parseInt(e.target.value) || 5))} /></Grid>
        <Grid item xs={6}><TextField fullWidth label="Draw Threshold (0-1)" type="number" value={drawThreshold} onChange={(e) => setDrawThreshold(Math.max(0, Math.min(1, parseFloat(e.target.value) || 0)))} /></Grid>
      </Grid>

      {/* Outcome circles */}
      <Box display="flex" flexWrap="wrap" justifyContent="space-around" mb={2}>
        <StatCircle label={`${homeName} 1H Win`} value={out1H.homeWin} drawThreshold={drawThreshold} />
        <StatCircle label={`1H Draw`} value={out1H.draw} isDraw drawThreshold={drawThreshold} />
        <StatCircle label={`${awayName} 1H Win`} value={out1H.awayWin} drawThreshold={drawThreshold} />
      </Box>

      <Box display="flex" flexWrap="wrap" justifyContent="space-around" mb={2}>
        <StatCircle label={`${homeName} 2H Win`} value={out2H.homeWin} drawThreshold={drawThreshold} />
        <StatCircle label={`2H Draw`} value={out2H.draw} isDraw drawThreshold={drawThreshold} />
        <StatCircle label={`${awayName} 2H Win`} value={out2H.awayWin} drawThreshold={drawThreshold} />
      </Box>

      <Box display="flex" flexWrap="wrap" justifyContent="space-around" mb={2}>
        <StatCircle label={`${homeName} FT Win`} value={outFT.homeWin} drawThreshold={drawThreshold} />
        <StatCircle label={`FT Draw`} value={outFT.draw} isDraw drawThreshold={drawThreshold} />
        <StatCircle label={`${awayName} FT Win`} value={outFT.awayWin} drawThreshold={drawThreshold} />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Form & Clean sheet circles */}
      <Box display="flex" flexWrap="wrap" justifyContent="space-around" mb={2}>
        <StatCircle label={`${homeName} 1H Form`} value={homeForm1H} drawThreshold={drawThreshold} />
        <StatCircle label={`${awayName} 1H Form`} value={awayForm1H} drawThreshold={drawThreshold} />
        <StatCircle label={`${homeName} 2H Form`} value={homeForm2H} drawThreshold={drawThreshold} />
        <StatCircle label={`${awayName} 2H Form`} value={awayForm2H} drawThreshold={drawThreshold} />
        <StatCircle label={`${homeName} Overall`} value={homeOverallForm} drawThreshold={drawThreshold} />
        <StatCircle label={`${awayName} Overall`} value={awayOverallForm} drawThreshold={drawThreshold} />
      </Box>

      <Box display="flex" flexWrap="wrap" justifyContent="space-around" mb={2}>
        <StatCircle label={`${homeName} 1H CS`} value={home1HCSprob} drawThreshold={drawThreshold} />
        <StatCircle label={`${awayName} 1H CS`} value={away1HCSprob} drawThreshold={drawThreshold} />
        <StatCircle label={`${homeName} 2H CS`} value={home2HCSprob} drawThreshold={drawThreshold} />
        <StatCircle label={`${awayName} 2H CS`} value={away2HCSprob} drawThreshold={drawThreshold} />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Clusters & Top predictions */}
      <Box mb={2}>
        <Typography variant="h6">1st Half Prediction</Typography>
        <Typography>Cluster: {renderCluster(cluster1H)}</Typography>
        {renderTopList("Top 1H Scores", top1H)}
      </Box>

      <Box mb={2}>
        <Typography variant="h6">2nd Half Prediction</Typography>
        <Typography>Cluster: {renderCluster(cluster2H)}</Typography>
        {renderTopList("Top 2H Scores", top2H)}
      </Box>

      <Box mb={2}>
        <Typography variant="h6">Fulltime Prediction</Typography>
        <Typography>Cluster: {renderCluster(clusterFT)}</Typography>
        {renderTopList("Top FT Scores", topFT)}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Tables */}
      {renderTable("Top FH Score Probabilities", scoreProbs1H)}
      {renderTable("Top SH Score Probabilities", scoreProbs2H)}
      {renderTable("Top FT Score Probabilities", scoreProbsFT)}
    </Box>
  );
}